import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, LogOut, Globe } from "lucide-react";
import { useI18n } from "@/i18n";
import { ThemeToggle, useTheme } from "@/context/theme";
import { StatusFooter } from "@/components/StatusFooter";

export const Shell = ({ navItems, user, onLogout, onLangSaved, basePath, brandLabel, children }) => {
  const { t, lang, setLang } = useI18n();
  const { theme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const toggleLang = () => {
    const next = lang === "es" ? "en" : "es";
    setLang(next);
    if (onLangSaved) onLangSaved(next);
  };

  const sidebarBg = "bg-card border-r border-border";
  const linkBase = "flex items-center gap-3 rounded-xl px-3 py-2.5 min-h-[44px] text-sm font-medium transition-colors";
  const linkActive = "bg-primary text-primary-foreground font-bold";
  const linkIdle = "text-muted-foreground hover:bg-secondary hover:text-foreground";
  const logo = theme === "dark" ? "/logos/rapidpay-white.png" : "/logos/rapidpay-color.webp";

  const NavList = ({ onNavigate, iconsOnly }) => (
    <nav className="flex-1 space-y-1 overflow-y-auto px-2">
      {navItems.map((item) => (
        <NavLink
          key={item.path}
          to={`${basePath}${item.path}`}
          end={item.path === ""}
          onClick={onNavigate}
          data-testid={`nav-${item.testId}`}
          title={t(item.label)}
          className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle} ${iconsOnly ? "justify-center px-0" : ""}`}
        >
          <item.icon className="w-5 h-5 shrink-0" />
          {!iconsOnly && <span className="truncate">{t(item.label)}</span>}
        </NavLink>
      ))}
    </nav>
  );

  const Footer = ({ iconsOnly }) => (
    <div className="px-2 pb-4 space-y-1">
      <ThemeToggle className={`w-full ${iconsOnly ? "justify-center px-0" : ""}`} label={!iconsOnly ? (theme === "dark" ? t("theme.light") : t("theme.dark")) : null} />
      <button onClick={toggleLang} data-testid="shell-language-toggle" className={`${linkBase} ${linkIdle} w-full ${iconsOnly ? "justify-center px-0" : ""}`}>
        <Globe className="w-5 h-5 shrink-0" />
        {!iconsOnly && <span>{lang === "es" ? "English" : "Español"}</span>}
      </button>
      <button onClick={onLogout} data-testid="shell-logout-button" className={`${linkBase} ${linkIdle} w-full ${iconsOnly ? "justify-center px-0" : ""}`}>
        <LogOut className="w-5 h-5 shrink-0" />
        {!iconsOnly && <span>{t("common.logout")}</span>}
      </button>
    </div>
  );

  const bottomItems = navItems.slice(0, 4);
  const activeTitle = navItems.find((i) => location.pathname === `${basePath}${i.path}` || (i.path === "" && location.pathname === basePath))?.label;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <aside className={`hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col ${sidebarBg} z-30`}>
        <div className="px-5 py-6">
          <img src={logo} alt="RapidPay" className="h-8" />
          {brandLabel && <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-[hsl(122,87%,30%)] dark:text-primary">{brandLabel}</p>}
        </div>
        <NavList />
        <Footer />
      </aside>

      <aside className={`hidden md:flex lg:hidden fixed inset-y-0 left-0 w-20 flex-col ${sidebarBg} z-30`}>
        <div className="flex justify-center py-6">
          <img src={logo} alt="RapidPay" className="h-6" />
        </div>
        <NavList iconsOnly />
        <Footer iconsOnly />
      </aside>

      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 h-14 bg-card/90 backdrop-blur-xl border-b border-border">
        <img src={logo} alt="RapidPay" className="h-6" />
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-button" className="w-11 h-11 flex items-center justify-center rounded-xl text-foreground hover:bg-secondary transition-colors" aria-label={t("nav.menu")}>
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-background flex flex-col" data-testid="mobile-menu-overlay">
          <div className="flex items-center justify-between px-4 h-14 border-b border-border">
            <img src={logo} alt="RapidPay" className="h-6" />
            <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" className="w-11 h-11 flex items-center justify-center rounded-xl text-foreground hover:bg-secondary transition-colors" aria-label="close">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1 flex flex-col pt-4 overflow-y-auto">
            <NavList onNavigate={() => setMenuOpen(false)} />
            <Footer />
          </div>
        </div>
      )}

      <main className="md:pl-20 lg:pl-64 pb-24 md:pb-0">
        <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-6xl">
          {activeTitle && <p className="text-xs font-semibold uppercase tracking-wider mb-1 text-muted-foreground">{user?.tenant?.name || brandLabel}</p>}
          {children}
          <StatusFooter user={user} className="mt-10" />
        </div>
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card/95 backdrop-blur-xl border-t border-border flex" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {bottomItems.map((item) => (
          <NavLink
            key={item.path}
            to={`${basePath}${item.path}`}
            end={item.path === ""}
            data-testid={`bottomnav-${item.testId}`}
            className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[10px] font-medium transition-colors ${isActive ? "text-[hsl(122,87%,30%)] dark:text-primary" : "text-muted-foreground"}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{t(item.label)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};
